import { useEffect, useMemo, useState } from 'react';
import * as providerAdminApi from '../../../api/providerAdminApi';
import { InviteDelivery } from './InviteDelivery';

/**
 * Invite a person as a Provider, Originator, Custodian or Investor user.
 *
 * The persona is the organization's type; the organization is only asked for
 * when more than one of that type is in scope. Access level maps to the two
 * membership roles that matter: `member` (does the work) and `admin` (also
 * manages the organization's people).
 *
 * Investor organizations are a workspace of their own: a platform-wide,
 * read-only view. Their members can never act, so access is fixed to `viewer`.
 */
export const INVESTOR_PERSONA = 'investor';
const INVESTOR_ROLE = 'viewer';

export const PERSONAS = [
  { value: 'provider', label: 'Provider' },
  { value: 'originator', label: 'Originator' },
  { value: 'custodian', label: 'Custodian' },
  { value: INVESTOR_PERSONA, label: 'Investor' },
];

export const ACCESS_LEVELS = [
  { value: 'member', label: 'Member', hint: 'Works in the workspace' },
  { value: 'admin', label: 'Admin', hint: 'Works in the workspace and manages its people' },
];

export function InvitePersonForm({ organizations, initialOrgId = '', onInvited }) {
  const orgs = useMemo(
    () => (Array.isArray(organizations) ? organizations : []).filter((org) => org?.id),
    [organizations]
  );
  const initialOrg = orgs.find((org) => org.id === initialOrgId) || null;

  const [persona, setPersona] = useState(initialOrg?.org_type || '');
  const [orgId, setOrgId] = useState(initialOrg?.id || '');
  const [email, setEmail] = useState('');
  const [access, setAccess] = useState('member');
  const [error, setError] = useState(null);
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);

  const isInvestor = persona === INVESTOR_PERSONA;
  const personas = PERSONAS.filter((item) => orgs.some((org) => org.org_type === item.value));
  const orgsForPersona = useMemo(
    () => orgs.filter((org) => org.org_type === persona),
    [orgs, persona]
  );

  useEffect(() => {
    if (!persona && personas.length > 0) {
      setPersona(initialOrg?.org_type || personas[0].value);
    }
  }, [persona, personas, initialOrg]);

  useEffect(() => {
    if (orgsForPersona.length === 0) {
      setOrgId('');
      return;
    }
    setOrgId((current) =>
      orgsForPersona.some((org) => org.id === current) ? current : orgsForPersona[0].id
    );
  }, [orgsForPersona]);

  const selectedOrg = orgsForPersona.find((org) => org.id === orgId) || null;
  const personaLabel = PERSONAS.find((item) => item.value === persona)?.label || persona;
  const accessHint = isInvestor
    ? 'Sees every organization, package, transaction and audit entry on the platform; cannot run any action'
    : ACCESS_LEVELS.find((item) => item.value === access)?.hint || '';
  const roleToSend = isInvestor ? INVESTOR_ROLE : access;

  async function handleSubmit(event) {
    event.preventDefault();
    if (!orgId) return;
    setError(null);
    setIssued(null);
    setBusy(true);
    try {
      const result = await providerAdminApi.createInvitation(orgId, { email, role: roleToSend });
      setIssued(result);
      setEmail('');
      if (onInvited) await onInvited(orgId, result);
    } catch (err) {
      setError(err.message || 'Invite failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <form
        onSubmit={handleSubmit}
        className="grid gap-3 rounded-2xl border border-eorig-border bg-white p-4 sm:grid-cols-4"
      >
        <label className="block text-xs text-eorig-muted">
          Invite as
          <select
            required
            aria-label="Persona"
            value={persona}
            onChange={(e) => setPersona(e.target.value)}
            disabled={personas.length === 0}
            className="mt-1 w-full rounded-xl border border-eorig-border px-3 py-2 text-sm text-eorig-ink disabled:opacity-60"
          >
            {personas.length === 0 ? <option value="">No organizations in scope</option> : null}
            {personas.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block text-xs text-eorig-muted">
          Organization
          {orgsForPersona.length > 1 ? (
            <select
              required
              aria-label="Organization"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              className="mt-1 w-full rounded-xl border border-eorig-border px-3 py-2 text-sm text-eorig-ink"
            >
              {orgsForPersona.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </select>
          ) : (
            <p className="mt-1 rounded-xl border border-eorig-border bg-eorig-surface px-3 py-2 text-sm text-eorig-ink">
              {selectedOrg?.name || '—'}
            </p>
          )}
        </label>
        <label className="block text-xs text-eorig-muted">
          Email
          <input
            required
            type="email"
            aria-label="Invite email"
            placeholder="person@institution.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-xl border border-eorig-border px-3 py-2 text-sm text-eorig-ink"
          />
        </label>
        <label className="block text-xs text-eorig-muted">
          Access
          {isInvestor ? (
            <p
              aria-label="Access level"
              className="mt-1 rounded-xl border border-eorig-border bg-eorig-surface px-3 py-2 text-sm text-eorig-ink"
            >
              Read-only
            </p>
          ) : (
            <select
              aria-label="Access level"
              value={access}
              onChange={(e) => setAccess(e.target.value)}
              className="mt-1 w-full rounded-xl border border-eorig-border px-3 py-2 text-sm text-eorig-ink"
            >
              {ACCESS_LEVELS.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
          )}
        </label>
        <p className="text-xs text-eorig-muted sm:col-span-3">
          {selectedOrg ? (
            <>
              Joins <span className="font-medium text-eorig-ink">{selectedOrg.name}</span> as{' '}
              {isInvestor ? 'an' : 'a'}{' '}
              <span className="font-medium text-eorig-ink">{isInvestor ? 'Investor' : personaLabel}</span>
              {isInvestor ? '' : ' user'}. {accessHint}.
            </>
          ) : (
            'Pick who the person should be.'
          )}
        </p>
        <div className="flex items-end">
          <button
            type="submit"
            disabled={!orgId || busy}
            className="w-full rounded-xl bg-[#003A70] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {busy ? 'Inviting…' : 'Invite'}
          </button>
        </div>
        {error ? (
          <p className="text-sm text-red-600 sm:col-span-4" role="alert">
            {error}
          </p>
        ) : null}
      </form>
      <InviteDelivery result={issued} />
    </div>
  );
}
